/* eslint-disable no-param-reassign */
import {
  Caption,
  Group,
  Loader,
  Option,
  Paragraph,
  Stack,
  Tooltip,
  VerticalSpacing,
} from "@einride/ui"
import { DeviceViewContainer } from "components/DeviceViewContainer/DeviceViewContainer"
import { FilterSelection } from "gen/graphql"
import { translateMetricId } from "lib/translations"
import { getFilterAttributeTitle } from "pages/ReportPage/components/Sidebar/FilterView"
import { getGroupColumnName } from "pages/ReportPage/components/Sidebar/translate"
import { useTranslation } from "react-i18next"

interface SidebarProps {
  onSidebarClick: () => void
  filterSelection: FilterSelection[]
  metricId?: string | null | undefined
  groupAttributeId?: string | null | undefined
  isOpen?: boolean
  loading: boolean
}

export const Sidebar = ({
  onSidebarClick,
  filterSelection,
  metricId,
  groupAttributeId,
  loading,
  isOpen = false,
}: SidebarProps): React.JSX.Element => {
  const { t } = useTranslation()

  return (
    <DeviceViewContainer
      isOpen={isOpen}
      onClose={() => {
        onSidebarClick()
      }}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          <VerticalSpacing size="xl" />
          {metricId && (
            <>
              <Paragraph color="secondary">{t("reportPage.metric")}</Paragraph>
              <VerticalSpacing size="xs" />
              <Tooltip
                content={t("reportPage.selectMetric")}
                triggerAsChild
                openDelayDuration={250}
              >
                <Option data-testid="select-metric-option" variant="secondary">
                  {translateMetricId(metricId, t)}
                </Option>
              </Tooltip>
            </>
          )}
          {filterSelection.length > 0 && (
            <>
              <VerticalSpacing size="md" />
              <Paragraph color="secondary">{t("reportPage.editFilters")}</Paragraph>
              <VerticalSpacing size="xs" />
              <Tooltip
                content={t("reportPage.filtersDescription")}
                triggerAsChild
                openDelayDuration={250}
              >
                <div>
                  {filterSelection.map((filter) => (
                    <Option key={filter.filterAttributeId} variant="secondary">
                      <Stack>
                        {getFilterAttributeTitle(t, filter.filterAttributeId)}
                        <Group>
                          {filter.filterTermIds.map((termId) => (
                            <Caption key={termId}>{termId}</Caption>
                          ))}
                        </Group>
                      </Stack>
                    </Option>
                  ))}
                </div>
              </Tooltip>
            </>
          )}

          {groupAttributeId && (
            <>
              <VerticalSpacing size="md" />
              <Paragraph color="secondary">{t("reportPage.groupMenuItem")}</Paragraph>
              <VerticalSpacing size="xs" />
              <Tooltip
                content={t("reportPage.groupsDescription")}
                triggerAsChild
                openDelayDuration={250}
              >
                <Option data-testid="select-group-option" variant="secondary">
                  {getGroupColumnName(groupAttributeId, t)}
                </Option>
              </Tooltip>
            </>
          )}
        </>
      )}
    </DeviceViewContainer>
  )
}
